import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateWineryDto, UpdateWineryDto, WineryDto } from '../../proxy/wineries/models';
import { WineryService } from '@proxy/wineries';
import { ActivatedRoute, Router } from '@angular/router';
import { CountryDto } from '@proxy/countries';
import { SelectOption } from 'src/app/shared/components/metro-form-select/metro-form-select.component';
import { firstValueFrom } from 'rxjs';
import { StorageService } from 'src/app/shared/services/storage.service';

/** winepassport#344 — máximo de imágenes en la galería secundaria de una bodega. */
const MAX_GALLERY = 3;

interface GalleryItem {
  /** id del FileAttachment ya subido (imágenes existentes al editar o recién subidas). */
  fileAttachmentId?: number;
  /** archivo local pendiente de subir (imágenes agregadas en esta edición). */
  file?: File;
  /** URL para el preview (blob local o de la imagen guardada). */
  previewUrl: string;
}

@Component({
  selector: 'app-winery-form',
  templateUrl: './winery-form.component.html',
  standalone: false
})
export class WineryFormComponent implements OnInit {
  @Input() winery?: WineryDto | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<WineryDto>();

  form!: FormGroup;
  isEditMode = false;

  countries: CountryDto[] = [];
  countryOptions: SelectOption[] = [];

  selectedFile: File | null = null;
  imagePreviewUrl: string | null = null;

  /** Galería secundaria (winepassport#344). Orden = posición en el array. */
  gallery: GalleryItem[] = [];
  readonly maxGallery = MAX_GALLERY;

  constructor(
    private fb: FormBuilder,
    private wineriesService: WineryService,
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService
  ) {
    this.buildForm();
  }

   ngOnInit(): void {
    this.loadCountries();

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.isEditMode = true;
    this.wineriesService.get(Number(id)).subscribe(winery => {
      this.winery = winery;
      this.patchForm(winery);

      if (winery.fileAttachmentId) {
        this.storageService.getImage(winery.fileAttachmentId).subscribe(blob => {
          this.imagePreviewUrl = URL.createObjectURL(blob);
        });
      }

      this.loadGallery(winery);
    });
  }


  private buildForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      country: [''],
      region: [''],
      subregion: [''],
      shortDescription: [''],
      description: [''],
      address: [''],
      googleMapsUrl: [''], // winepassport#377 — link de Google Maps (opcional)
      contactName: [''],
      email: ['', [Validators.email]],
      phone: [''],
      website: [''],
      fileAttachmentId: [''] // cambio imageUrl → fileAttachmentId
    });
  }

  private patchForm(winery: WineryDto) {
    this.isEditMode = true;
    this.form.patchValue(winery);
  }

  private loadGallery(winery: WineryDto) {
    const imgs = [...(winery.images ?? [])].sort((a, b) => a.order - b.order);
    this.gallery = imgs.map(i => ({ fileAttachmentId: i.fileAttachmentId, previewUrl: '' }));
    imgs.forEach((i, idx) => {
      this.storageService.getImage(i.fileAttachmentId).subscribe(blob => {
        if (this.gallery[idx]) this.gallery[idx].previewUrl = URL.createObjectURL(blob);
      });
    });
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired';
      if (control.errors?.['email']) return '::InvalidEmail';
    }
    return undefined;
  }

   async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    let newFileAttachmentId: number | null = null;

    if (this.selectedFile) {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      newFileAttachmentId = await firstValueFrom(
        this.wineriesService.uploadFileByFile(formData)
      );
    }

    // Galería: subir las imágenes nuevas y armar la lista de ids en orden (winepassport#344).
    const galleryFileIds: number[] = [];
    for (const item of this.gallery.slice(0, MAX_GALLERY)) {
      if (item.fileAttachmentId != null) {
        galleryFileIds.push(item.fileAttachmentId);
      } else if (item.file) {
        const fd = new FormData();
        fd.append('file', item.file);
        const uploadedId = await firstValueFrom(this.wineriesService.uploadFileByFile(fd));
        galleryFileIds.push(uploadedId);
      }
    }

    // getRawValue en vez de form.value: incluye cualquier control que pudiera estar
    // deshabilitado, así el DTO no pierde campos silenciosamente.
    const dto = this.form.getRawValue() as CreateWineryDto | UpdateWineryDto;
    dto.fileAttachmentId = newFileAttachmentId ?? this.winery?.fileAttachmentId;
    dto.galleryFileIds = galleryFileIds;

    // winepassport#377/#378 — campos opcionales de texto: mandar undefined en vez de '' para
    // no guardar strings vacíos ni chocar con validaciones de formato en el back.
    for (const key of ['website', 'googleMapsUrl', 'address', 'description', 'shortDescription'] as const) {
      if (typeof (dto as any)[key] === 'string' && (dto as any)[key].trim() === '') {
        (dto as any)[key] = undefined;
      }
    }

    const oldFileAttachmentId = this.winery?.fileAttachmentId;

    if (this.isEditMode && this.winery) {
      this.wineriesService.update(this.winery.id, dto as UpdateWineryDto).subscribe(result => {
        if (newFileAttachmentId && oldFileAttachmentId) {
          this.wineriesService.deleteFileByFileAttachmentId(oldFileAttachmentId).subscribe();
        }
        this.handleSuccess(result);
      });
    } else {
      this.wineriesService.create(dto as CreateWineryDto).subscribe(result => this.handleSuccess(result));
    }
  }

  private handleSuccess(result: WineryDto) {
    this.saved.emit(result);
    this.resetForm();
    this.router.navigate(['/wineries']);

  }

  resetForm() {
    this.form.reset();
    this.isEditMode = false;
    this.winery = null;
    this.gallery = [];
  }

  loadCountries(): void {
    this.wineriesService.getCountries().subscribe(countries => {
      this.countries = countries;
      this.countryOptions = countries.map(c => ({
        value: c.name,
        label: c.name
      }));
    });
  }

  onCountryChange(value: string) {
    this.form.get('country')?.setValue(value);
  }

  onImageSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  // ── Galería secundaria ──────────────────────────────────────────────
  onGallerySelected(files: File[]): void {
    if (!files?.length) return;
    const room = MAX_GALLERY - this.gallery.length;
    for (const file of files.slice(0, Math.max(0, room))) {
      this.gallery.push({ file, previewUrl: URL.createObjectURL(file) });
    }
  }

  removeGalleryItem(index: number): void {
    this.gallery.splice(index, 1);
  }

  moveGalleryItem(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.gallery.length) return;
    const [item] = this.gallery.splice(index, 1);
    this.gallery.splice(target, 0, item);
  }
}
